export const MAX_OUT_OF_BUDGET_INCREASE_PERCENT = 1000;

export type OutOfBudgetBoostIncreaseMode = 'PERCENT' | 'VALUE';

export type OutOfBudgetBoostSettings = {
  enabled: boolean;
  increaseMode: OutOfBudgetBoostIncreaseMode;
  increaseValue: number | null;
  ceilingBudget: number | null;
};

export type OutOfBudgetBoostSavePayload = {
  enabled: boolean;
  increaseMode: OutOfBudgetBoostIncreaseMode;
  increaseValue: number;
  ceilingBudget: number;
};

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return error instanceof Error ? error.message : fallback;
}

function parseIncreaseMode(raw: unknown): OutOfBudgetBoostIncreaseMode {
  return String(raw || 'PERCENT').toUpperCase() === 'VALUE' ? 'VALUE' : 'PERCENT';
}

export function parseOutOfBudgetBoostSettings(raw: unknown): OutOfBudgetBoostSettings {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const increase = Number(
    data.increaseValue != null ? data.increaseValue : data.increasePercent
  );
  const ceiling = Number(data.ceilingBudget);
  return {
    enabled: data.enabled === true,
    increaseMode: parseIncreaseMode(data.increaseMode),
    increaseValue: Number.isFinite(increase) ? increase : null,
    ceilingBudget: Number.isFinite(ceiling) ? ceiling : null,
  };
}

export function validateOutOfBudgetBoostForm(input: {
  increaseMode: OutOfBudgetBoostIncreaseMode;
  increaseValue: string;
  ceilingBudget: string;
}): string | null {
  const amount = Number(input.increaseValue);
  const isPercent = input.increaseMode === 'PERCENT';
  if (!input.increaseValue.trim() || !Number.isFinite(amount) || amount <= 0) {
    return isPercent
      ? 'Increase percent must be greater than 0'
      : 'Increase amount must be greater than 0';
  }
  if (isPercent && amount > MAX_OUT_OF_BUDGET_INCREASE_PERCENT) {
    return `Increase percent must be at most ${MAX_OUT_OF_BUDGET_INCREASE_PERCENT}`;
  }
  const ceiling = Number(input.ceilingBudget);
  if (!input.ceilingBudget.trim() || !Number.isFinite(ceiling) || ceiling <= 0) {
    return 'Ceiling budget must be greater than 0';
  }
  return null;
}

export function buildOutOfBudgetBoostSavePayload(input: {
  enabled: boolean;
  increaseMode: OutOfBudgetBoostIncreaseMode;
  increaseValue: string;
  ceilingBudget: string;
}): OutOfBudgetBoostSavePayload {
  return {
    enabled: input.enabled,
    increaseMode: input.increaseMode,
    increaseValue: Number(input.increaseValue),
    ceilingBudget: Number(input.ceilingBudget),
  };
}
